import { Component, ElementRef, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { LoginService } from 'src/app/services/login/login.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service';
import { EMAIL_REGEX } from 'src/app/utils/constante';

@Component({
  selector: 'app-renew-password',
  templateUrl: './renew-password.component.html',
  styleUrls: ['./renew-password.component.scss']
})
export class RenewPasswordComponent implements OnInit {

  public passwordForm: FormGroup;
  public errorLogin = false;

  constructor(
    private formBuilder: FormBuilder,
    public router: Router,
    public viewContainerRef: ViewContainerRef,
    public modalService: ModalService,
    public loginService: LoginService,
    private activatedRoute: ActivatedRoute,
    private _snack: SnackbarService
  ) { }

  ngOnInit(): void {
    this.passwordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, this.passwordStrengthValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validator: this.checkIfMatchingPasswords('newPassword', 'confirmPassword') });
    const email = this.activatedRoute.snapshot.paramMap.get('email');
    if (email) {
      this.passwordForm.get('email').setValue(email);
    }
  }

  private passwordStrengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    const valid =
      value.length >= 10 &&
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[^A-Za-z0-9]/.test(value);
    return valid ? null : { passwordStrength: true };
  };

  get newPasswordControl(): AbstractControl {
    return this.passwordForm.get('newPassword');
  }

  get passwordRules(): { label: string, valid: boolean }[] {
    const value = this.newPasswordControl?.value || '';
    return [
      { label: 'Au moins 10 caractères', valid: value.length >= 10 },
      { label: 'Au moins une lettre majuscule', valid: /[A-Z]/.test(value) },
      { label: 'Au moins une lettre minuscule', valid: /[a-z]/.test(value) },
      { label: 'Au moins un chiffre', valid: /[0-9]/.test(value) },
      { label: 'Au moins un caractère spécial', valid: /[^A-Za-z0-9]/.test(value) },
    ];
  }

  private checkIfMatchingPasswords(passwordKey: string, passwordConfirmationKey: string): (group: FormGroup) => void {
    return (group: FormGroup) => {
      const passwordInput = group.controls[passwordKey]
      const passwordConfirmationInput = group.controls[passwordConfirmationKey];
      if (passwordInput.value !== passwordConfirmationInput.value) {
        return passwordConfirmationInput.setErrors({ notEquivalent: true });
      }
      else {
        return passwordConfirmationInput.setErrors(null);
      }
    };
  }

  public async onSubmit(): Promise<void> {
    this.passwordForm.markAllAsTouched();
    if (this.passwordForm.valid) {
      const loader = this.modalService.openLoading(this.viewContainerRef);
      try {
        await this.loginService.renewPassword(this.passwordForm.get('email').value, this.passwordForm.get('oldPassword').value, this.passwordForm.get('newPassword').value);
        this.modalService.close(loader);
        const modal = this.modalService.openAlert(this.viewContainerRef, 'Succès', 'Votre mot de passe a bien été mis à jour !');
        const sub = modal.instance.closeEmitter.subscribe(() => {
          this.router.navigateByUrl('');
          sub.unsubscribe();
        });
      } catch (e) {
        this._snack.openSnackBarError(e.error?.message || e.message || 'Une erreur est survenue lors du renouvellement de votre mot de passe', undefined, undefined, 10000);
        this.modalService.close(loader);
        throw e;
      }
    }
  }

}
